import { ActionIcon, Button, Group, Popover, Stack, VisuallyHidden } from "@mantine/core";
import { ListFilter, RefreshCw } from "lucide-react";
import { useId, useState } from "react";

import {
  ActorFacets,
  NO_ACTOR_FILTER,
  filtered,
  type ActorFilter,
} from "../../../platform/actors/ActorFacets";
import { ROOM_EITHER } from "../../../ui/popover-room";
import classes from "./actors.module.css";

export interface ActorFiltersProps {
  applied: ActorFilter;
  onApply: (filter: ActorFilter) => void;
}

/**
 * Live's filter on the Global Actors (`global-filters-form`, capture 09): Gender, Age, Skin Tone
 * (six swatches), Situation, Clothing and Accessories, each several values or none. Apply sets
 * them and closes; Reset clears them and closes. The button carries live's dot while one is set.
 */
export function ActorFilters({ applied, onApply }: ActorFiltersProps) {
  const [opened, setOpened] = useState(false);
  const [draft, setDraft] = useState<ActorFilter>(applied);
  const titleId = useId();
  const apply = (filter: ActorFilter) => {
    onApply(filter);
    setOpened(false);
  };
  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="bottom-start"
      shadow="md"
      middlewares={ROOM_EITHER}
      trapFocus
      returnFocus
    >
      <Popover.Target>
        <ActionIcon
          variant="default"
          size={36}
          className={classes.filter}
          aria-label="Filters"
          onClick={() => {
            setDraft(applied);
            setOpened((open) => !open);
          }}
        >
          <ListFilter size={24} strokeWidth={1.3} />
          {filtered(applied) ? <span className={classes.filterDot} aria-hidden /> : null}
        </ActionIcon>
      </Popover.Target>
      <Popover.Dropdown aria-labelledby={titleId} className={classes.filters}>
        <VisuallyHidden id={titleId}>Filters</VisuallyHidden>
        <Stack gap="md">
          <ActorFacets value={draft} onChange={setDraft} />
          <Group grow gap={8} className={classes.filtersFooter}>
            <Button onClick={() => apply(draft)}>Apply</Button>
            <Button
              variant="default"
              leftSection={<RefreshCw size={16} />}
              onClick={() => {
                setDraft(NO_ACTOR_FILTER);
                apply(NO_ACTOR_FILTER);
              }}
            >
              Reset
            </Button>
          </Group>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}
